import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../../context/useAuth'
import { useWorkspace } from '../../../context/useWorkspace'
import { useMyAccessScope } from '../../../hooks/useMyAccessScope'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog, Button, Select, TextInput } from '../../Common'
import { StatusPill } from '../../Common/StatusPill'
import { OpsRecordModalForm } from '../../Ops/registers/OpsRecordModalForm'
import { RecordView } from '../../Ops/registers/RecordView'
import {
  reviewRecord,
  approveRecord,
  returnRecord,
  coSignRecord,
  subscribeToRecords,
  subscribeToRecordsAwaitingCoSign,
  subscribeToMySectorRoles,
  subscribeToTemplates,
} from '../../../services/opsRegistersService'
import {
  subscribeRosters,
  subscribeRosterVersions,
  subscribeShifts,
  subscribeRosterChanges,
  subscribeAttendance,
  subscribeAttendanceCorrections,
  acknowledgeRoster,
  decideRosterChange,
  decideAttendanceCorrection,
} from '../../../services/opsRostersService'
import {
  subscribeOpsRequests,
  acknowledgeRequest,
  startRequest,
  markRequestDone,
  verifyRequest,
  reopenRequest,
  cancelRequest,
} from '../../../services/opsRequestsService'
import { subscribeDues } from '../../../services/opsSchedulesService'
import { subscribeToWorkspacePlaces } from '../../../services/opsPlacesService'
import { subscribeOpsAssets, releaseAsset } from '../../../services/opsAssetsService'
import { subscribeToBedStates, setBedState } from '../../../services/opsBedStatesService'
import { assetActionsFor } from '../../../services/assetActionsFor'
import { BmwPhoneSection } from '../../Ops/hospital/BmwPhoneSection'
import { OnCallPhoneSection } from '../../Ops/hospital/OnCallPhoneSection'
import { MemberDprPhoneSection } from '../../Ops/construction/MemberDprPhoneSection'
import { MemberInspectionsPhoneSection } from '../../Ops/construction/MemberInspectionsPhoneSection'
import { PlantMemberPhoneSection } from '../../Ops/plant/PlantMemberPhoneSection'
import { MemberConApprovalsGroup } from '../../Ops/construction/MemberConApprovalsGroup'
import { MemberConReportsGroup } from '../../Ops/construction/MemberConReportsGroup'
import { isModuleEnabled } from '../../../utils/workspaceModules'
import { unitExceptions, unitSubtreeIds } from '../../Ops/hospital/hospitalTodayHelpers'
import { canManageBedBoard, canOverrideBedReady } from '../../Ops/hospital/bedBoardHelpers'
import {
  getMemberOpsPhoneItems,
  subscribeMemberOpsStore,
} from '../../../services/member/memberOpsStore'
import { requestActionsFor } from '../../../services/requestActionsFor'
import { needsCallerAcknowledgement } from '../../../utils/opsRosterGrid'
import { isAttendanceEffective } from '../../../types/ops'
import { isWorkspaceOwner } from '../../../utils/workspaceOwners'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import type {
  OpsRecord,
  OpsRoster,
  OpsRosterVersion,
  OpsRosterChange,
  OpsAttendance,
  OpsAttendanceCorrection,
  OpsRequest,
  OpsDue,
  OpsPlace,
  OpsShift,
  OpsTemplate,
  OpsSectorRole,
  OpsAsset,
  OpsBedState,
} from '../../../types/ops'
import { OPS_REQUEST_CLOSED_STATES } from '../../../types/ops'
import type { OpsPhoneItemKey } from '../../../../functions-common/src/ops/phoneItems'
import styles from './MemberOpsTab.module.css'

function createOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export interface MemberOpsTabProps {
  workspaceId: string
  initialRequestId?: string
  requestId?: string
}

function getThisWeekAndNextRange(): { startDayKey: string; endDayKey: string } {
  const d = new Date()
  const day = d.getDay() // 0 = Sun, 1 = Mon...
  const diffToMonday = (day + 6) % 7
  const monday = new Date(d)
  monday.setDate(d.getDate() - diffToMonday)
  const endNextWeek = new Date(monday)
  endNextWeek.setDate(monday.getDate() + 13)

  const format = (date: Date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const dt = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${dt}`
  }

  return { startDayKey: format(monday), endDayKey: format(endNextWeek) }
}

function getTodayDayKey(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dt = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dt}`
}

function getTodayRange(): { from: number; to: number } {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date()
  end.setHours(23, 59, 59, 999)
  return { from: start.getTime(), to: end.getTime() }
}

function getPastSevenDaysRange(): { startDayKey: string; endDayKey: string } {
  const d = new Date()
  const end = new Date(d)
  const start = new Date(d)
  start.setDate(d.getDate() - 6)

  const format = (date: Date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const dt = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${dt}`
  }

  return { startDayKey: format(start), endDayKey: format(end) }
}

function formatShiftMinutes(m?: number): string {
  if (typeof m !== 'number') return ''
  const h = Math.floor(m / 60)
  const min = m % 60
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

export function MemberOpsTab({
  workspaceId,
  initialRequestId: propInitialRequestId,
  requestId: propRequestId,
}: MemberOpsTabProps): React.ReactElement {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { workspaces, activeWorkspaceId, activeRole } = useWorkspace()
  const scope = useMyAccessScope(workspaceId)
  const currentUid = currentUser?.uid || ''

  const ws = useMemo(
    () =>
      workspaces.find((w) => w.id === workspaceId) ||
      workspaces.find((w) => w.id === activeWorkspaceId),
    [workspaces, workspaceId, activeWorkspaceId],
  )
  const isOwner = Boolean(ws && currentUid && isWorkspaceOwner(ws, currentUid))
  const isAdmin = activeRole === 'ADMIN' || activeRole === 'OWNER' || isOwner
  const isConstruction = isModuleEnabled(ws, 'construction')

  // Deep-link targeted request (listens to prop updates & route changes)
  const [urlRequestId, setUrlRequestId] = useState<string | undefined>(() => {
    if (typeof window !== 'undefined' && window.location) {
      const sp = new URLSearchParams(window.location.search)
      return sp.get('request') || undefined
    }
    return undefined
  })

  useEffect(() => {
    const handlePopState = () => {
      if (typeof window !== 'undefined' && window.location) {
        const sp = new URLSearchParams(window.location.search)
        setUrlRequestId(sp.get('request') || undefined)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const effectiveRequestId = propRequestId ?? propInitialRequestId ?? urlRequestId

  // Allowed items
  const [allowedItems, setAllowedItems] = useState<OpsPhoneItemKey[]>(() =>
    getMemberOpsPhoneItems(workspaceId),
  )

  useEffect(() => {
    const unsub = subscribeMemberOpsStore((snap) => {
      const items = snap.opsPhoneItemsByWorkspace[workspaceId] ?? snap.opsPhoneItems
      setAllowedItems(items ?? [])
    })
    return () => unsub()
  }, [workspaceId])

  const canApprovals = allowedItems.includes('m.approvals')
  const canRoster = allowedItems.includes('m.roster')
  const canRegisters = allowedItems.includes('m.registers')
  const canRequests = allowedItems.includes('m.requests')
  const canReports = allowedItems.includes('m.reports')
  const canExceptions = allowedItems.includes('m.exceptions')
  const canEquipment = allowedItems.includes('m.equipment')
  const canBeds = allowedItems.includes('m.beds')
  const canBmw = allowedItems.includes('m.bmw')
  const canOnCall = allowedItems.includes('m.oncall')
  const canDpr = allowedItems.includes('m.dpr')
  const canInspections = allowedItems.includes('m.inspections')
  const canPlant = allowedItems.includes('m.plant')

  // Sector Roles
  const [mySectorRoles, setMySectorRoles] = useState<OpsSectorRole[]>([])
  useEffect(() => {
    if (!workspaceId || !currentUid) return
    const unsub = subscribeToMySectorRoles(
      workspaceId,
      currentUid,
      ({ roleKeys }) => setMySectorRoles(roleKeys as OpsSectorRole[]),
      (err) => console.error('[MemberOpsTab] subscribeToMySectorRoles failed', err),
    )
    return () => unsub()
  }, [workspaceId, currentUid])

  const canReviewRecord =
    isAdmin ||
    mySectorRoles.includes('RECORD_REVIEWER') ||
    mySectorRoles.includes('RECORD_APPROVER')
  const canApproveRecord = isAdmin || mySectorRoles.includes('RECORD_APPROVER')
  const isManager = isAdmin || mySectorRoles.includes('ROSTER_MANAGER')
  const isMarker = isManager || mySectorRoles.includes('ATTENDANCE_MARKER')
  const canCorrectBmw = isAdmin || mySectorRoles.includes('COMPLIANCE_MANAGER')

  // Common UI State
  const [actionBusy, setActionBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [approvalsError, setApprovalsError] = useState<string | null>(null)
  const [rosterError, setRosterError] = useState<string | null>(null)
  const [registersError, setRegistersError] = useState<string | null>(null)
  const [requestsError, setRequestsError] = useState<string | null>(null)
  const [todayError, setTodayError] = useState<string | null>(null)
  const [exceptionsError, setExceptionsError] = useState<string | null>(null)
  const [equipmentError, setEquipmentError] = useState<string | null>(null)
  const [bedsError, setBedsError] = useState<string | null>(null)

  // Places
  const [places, setPlaces] = useState<OpsPlace[]>([])
  useEffect(() => {
    if (!workspaceId || !scope.ready) return
    const unsub = subscribeToWorkspacePlaces(
      workspaceId,
      (p) => setPlaces(p),
      (err) => console.error('[MemberOpsTab] subscribeToWorkspacePlaces failed', err),
      { callerUid: currentUid, isRestricted: scope.restricted },
    )
    return () => unsub()
  }, [workspaceId, currentUid, scope.ready, scope.restricted])

  const allowedPlaceIds = useMemo(
    () => new Set(places.map((p) => p.id).filter((id): id is string => Boolean(id))),
    [places],
  )
  const effectiveScope = useMemo(
    () => ({
      ready: scope.ready,
      restricted: scope.restricted,
      allowedPlaceIds,
    }),
    [scope.ready, scope.restricted, allowedPlaceIds],
  )

  const unitPlaces = useMemo(() => {
    return places.filter(
      (p) => !p.archived && (p.level === 'DEPARTMENT' || p.level === 'UNIT' || p.level === 'WARD'),
    )
  }, [places])

  const [selectedUnitId, setSelectedUnitId] = useState<string>('')
  const activeUnitId = useMemo(() => {
    if (selectedUnitId && unitPlaces.some((u) => u.id === selectedUnitId)) {
      return selectedUnitId
    }
    return unitPlaces[0]?.id || ''
  }, [selectedUnitId, unitPlaces])

  const activeUnitSubtreeIds = useMemo(() => {
    return unitSubtreeIds(places, activeUnitId)
  }, [places, activeUnitId])

  // Subscriptions for Exceptions & Equipment
  const [exceptionAssets, setExceptionAssets] = useState<OpsAsset[]>([])
  useEffect(() => {
    if (!workspaceId || (!canExceptions && !canEquipment) || !scope.ready) return
    const unsub = subscribeOpsAssets(
      workspaceId,
      { callerUid: currentUid, isRestricted: scope.restricted, includeRetired: false },
      (list) => {
        setExceptionAssets(list)
        if (canExceptions) setExceptionsError(null)
        if (canEquipment) setEquipmentError(null)
      },
      (err) => {
        console.error('[MemberOpsTab] subscribeOpsAssets failed', err)
        const msg = getApiErrorMessage(err)
        if (canExceptions) setExceptionsError(msg)
        if (canEquipment) setEquipmentError(msg)
      },
    )
    return () => unsub()
  }, [workspaceId, canExceptions, canEquipment, currentUid, scope.ready, scope.restricted])

  const [exceptionBedStates, setExceptionBedStates] = useState<OpsBedState[]>([])
  useEffect(() => {
    if (!workspaceId || !canExceptions || !scope.ready) return
    const unsub = subscribeToBedStates(
      workspaceId,
      null,
      (list) => {
        setExceptionBedStates(list)
        setExceptionsError(null)
      },
      (err) => {
        console.error('[MemberOpsTab] subscribeToBedStates failed', err)
        setExceptionsError(getApiErrorMessage(err))
      },
      { callerUid: currentUid, isRestricted: scope.restricted },
    )
    return () => unsub()
  }, [workspaceId, canExceptions, currentUid, scope.ready, scope.restricted])

  // ---------------------------------------------------------------------------
  // Bed Board State (m.beds)
  // ---------------------------------------------------------------------------
  const memberWards = useMemo(() => {
    return places.filter((p) => p.level === 'WARD')
  }, [places])

  const [selectedWardId, setSelectedWardId] = useState<string>('')
  const activeWardId = useMemo(() => {
    if (selectedWardId && memberWards.some((w) => w.id === selectedWardId)) {
      return selectedWardId
    }
    return memberWards[0]?.id || ''
  }, [selectedWardId, memberWards])

  const selectedWard = useMemo(() => {
    return memberWards.find((w) => w.id === activeWardId) || null
  }, [memberWards, activeWardId])

  const [memberBedStates, setMemberBedStates] = useState<OpsBedState[]>([])
  useEffect(() => {
    if (!workspaceId || !canBeds || !scope.ready) return
    const unsub = subscribeToBedStates(
      workspaceId,
      activeWardId || null,
      (list) => {
        setMemberBedStates(list)
        setBedsError(null)
      },
      (err) => {
        console.error('[MemberOpsTab] subscribeToBedStates failed', err)
        setBedsError(getApiErrorMessage(err))
      },
      { callerUid: currentUid, isRestricted: scope.restricted },
    )
    return () => unsub()
  }, [workspaceId, canBeds, activeWardId, currentUid, scope.ready, scope.restricted])

  const { roomsInWard, bedsInWard } = useMemo(() => {
    if (!activeWardId) {
      return { roomsInWard: [], bedsInWard: [] }
    }
    const rooms = places.filter(
      (p) =>
        p.level === 'ROOM' &&
        (p.parentId === activeWardId || p.path?.includes(activeWardId)),
    )
    const roomIds = new Set(rooms.map((r) => r.id))
    const beds = places.filter(
      (p) =>
        p.level === 'BED' &&
        (roomIds.has(p.parentId || '') || p.path?.includes(activeWardId)),
    )
    return { roomsInWard: rooms, bedsInWard: beds }
  }, [places, activeWardId])

  const bedStateMap = useMemo(() => {
    const map = new Map<string, OpsBedState>()
    for (const bs of memberBedStates) {
      map.set(bs.placeId, bs)
    }
    return map
  }, [memberBedStates])

  const bedCounts = useMemo(() => {
    const c: Record<string, number> = {
      ALL: bedsInWard.length,
      READY: 0,
      OCCUPIED: 0,
      AWAITING_CLEAN: 0,
      CLEANING: 0,
      BLOCKED: 0,
    }
    for (const b of bedsInWard) {
      const s = (b.id ? bedStateMap.get(b.id)?.state : undefined) || 'READY'
      c[s] = (c[s] || 0) + 1
    }
    return c
  }, [bedsInWard, bedStateMap])

  const canManageBeds = useMemo(() => {
    return canManageBedBoard({ role: activeRole, sectorRoles: mySectorRoles }, selectedWard)
  }, [activeRole, mySectorRoles, selectedWard])

  const canOverrideReady = useMemo(() => {
    return canOverrideBedReady({ role: activeRole })
  }, [activeRole])

  const [bedDischargeTarget, setBedDischargeTarget] = useState<OpsPlace | null>(null)
  const [bedBlockTarget, setBedBlockTarget] = useState<OpsPlace | null>(null)
  const [bedBlockReason, setBedBlockReason] = useState('')
  const [bedOverrideTarget, setBedOverrideTarget] = useState<OpsPlace | null>(null)
  const [bedOverrideReason, setBedOverrideReason] = useState('')
  const [bedActionBusy, setBedActionBusy] = useState(false)

  const handleMarkOccupied = async (placeId: string) => {
    setBedActionBusy(true)
    try {
      await setBedState({
        workspaceId,
        placeId,
        action: 'MARK_OCCUPIED',
      })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBedActionBusy(false)
    }
  }

  const handleUnblock = async (placeId: string) => {
    setBedActionBusy(true)
    try {
      await setBedState({
        workspaceId,
        placeId,
        action: 'UNBLOCK',
      })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBedActionBusy(false)
    }
  }

  const handleConfirmDischarge = async () => {
    if (!bedDischargeTarget?.id) return
    setBedActionBusy(true)
    try {
      await setBedState({
        workspaceId,
        placeId: bedDischargeTarget.id,
        action: 'DISCHARGE',
      })
      setBedDischargeTarget(null)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBedActionBusy(false)
    }
  }

  const handleConfirmBlock = async () => {
    if (!bedBlockTarget?.id || bedBlockReason.trim().length < 3) return
    setBedActionBusy(true)
    try {
      await setBedState({
        workspaceId,
        placeId: bedBlockTarget.id,
        action: 'BLOCK',
        reason: bedBlockReason.trim(),
      })
      setBedBlockTarget(null)
      setBedBlockReason('')
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBedActionBusy(false)
    }
  }

  const handleConfirmOverride = async () => {
    if (!bedOverrideTarget?.id || bedOverrideReason.trim().length < 3) return
    setBedActionBusy(true)
    try {
      await setBedState({
        workspaceId,
        placeId: bedOverrideTarget.id,
        action: 'OVERRIDE_READY',
        reason: bedOverrideReason.trim(),
      })
      setBedOverrideTarget(null)
      setBedOverrideReason('')
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBedActionBusy(false)
    }
  }

  // ---------------------------------------------------------------------------
  // 1. APPROVALS SECTION
  // ---------------------------------------------------------------------------
  const [submittedRecords, setSubmittedRecords] = useState<OpsRecord[]>([])
  const [cosignRecords, setCosignRecords] = useState<OpsRecord[]>([])
  const [pendingChanges, setPendingChanges] = useState<OpsRosterChange[]>([])
  const [pendingCorrections, setPendingCorrections] = useState<
    Array<{ attendanceId: string; correction: OpsAttendanceCorrection }>
  >([])

  // Modal dialog state for return / self-approval
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [reasonTitle, setReasonTitle] = useState('')
  const [reasonValue, setReasonValue] = useState('')
  const [onConfirmReason, setOnConfirmReason] = useState<(() => Promise<void>) | null>(null)

  useEffect(() => {
    if (!workspaceId || !canApprovals || !scope.ready) return
    const unsub = subscribeToRecords(
      workspaceId,
      {
        state: 'SUBMITTED',
        callerUid: currentUid,
        isReviewer: canReviewRecord,
        isEditor: isManager,
        isRestricted: scope.restricted,
      },
      (recs: OpsRecord[]) => setSubmittedRecords(recs),
      (err: Error) => setApprovalsError(getApiErrorMessage(err)),
    )
    return () => unsub()
  }, [workspaceId, canApprovals, currentUid, canReviewRecord, isManager, scope.ready, scope.restricted])

  useEffect(() => {
    if (!workspaceId || !canApprovals || !currentUid || !scope.ready) return
    const unsub = subscribeToRecordsAwaitingCoSign(
      workspaceId,
      currentUid,
      {
        callerUid: currentUid,
        isReviewer: canReviewRecord,
        isEditor: isManager,
        isRestricted: scope.restricted,
      },
      (recs: OpsRecord[]) => setCosignRecords(recs),
      (err: Error) => setApprovalsError(getApiErrorMessage(err)),
    )
    return () => unsub()
  }, [workspaceId, canApprovals, currentUid, canReviewRecord, isManager, scope.ready, scope.restricted])

  useEffect(() => {
    if (!workspaceId || !canApprovals || !isManager || !scope.ready) return
    const unsub = subscribeRosterChanges(
      workspaceId,
      { callerUid: currentUid, isManager: true, isRestricted: scope.restricted },
      (changes) => {
        const waiting = changes.filter(
          (c) =>
            (c.type === 'LEAVE' && c.state === 'REQUESTED') ||
            (c.type !== 'LEAVE' && c.state === 'ACCEPTED'),
        )
        setPendingChanges(waiting)
      },
      (err) => setApprovalsError(getApiErrorMessage(err)),
    )
    return () => unsub()
  }, [workspaceId, canApprovals, currentUid, isManager, scope.ready, scope.restricted])

  // Decisions
  const handleReviewRecord = async (recordId: string) => {
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await reviewRecord({ workspaceId, recordId })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleApproveRecord = async (record: OpsRecord) => {
    const isSelf = record.authorUid === currentUid
    if (isSelf && !isOwner) {
      setErrorMsg(t('NLSOpsCannotReviewOwnRecord'))
      return
    }

    if (isSelf && isOwner) {
      setReasonTitle(t('NLSMemberOpsSelfApprovalReason'))
      setReasonValue('')
      setOnConfirmReason(() => async () => {
        await approveRecord({
          workspaceId,
          recordId: record.id || '',
          selfApprovalReason: reasonValue.trim(),
        })
      })
      setReasonDialogOpen(true)
      return
    }

    setActionBusy(true)
    setErrorMsg(null)
    try {
      await approveRecord({ workspaceId, recordId: record.id || '' })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleReturnRecordPrompt = (recordId: string) => {
    setReasonTitle(t('NLSMemberOpsReturnReason'))
    setReasonValue('')
    setOnConfirmReason(() => async () => {
      await returnRecord({
        workspaceId,
        recordId,
        reason: reasonValue.trim(),
      })
    })
    setReasonDialogOpen(true)
  }

  const handleCoSign = async (recordId: string, agree: boolean) => {
    if (!agree) {
      setReasonTitle(t('NLSMemberOpsReturnReason'))
      setReasonValue('')
      setOnConfirmReason(() => async () => {
        await coSignRecord({
          workspaceId,
          recordId,
          agree: false,
          reason: reasonValue.trim(),
          opId: crypto.randomUUID(),
        })
      })
      setReasonDialogOpen(true)
      return
    }

    setActionBusy(true)
    setErrorMsg(null)
    try {
      await coSignRecord({
        workspaceId,
        recordId,
        agree: true,
        opId: crypto.randomUUID(),
      })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleDecideChange = async (change: OpsRosterChange, decision: 'APPROVE' | 'REJECT') => {
    const isSelf = change.requesterUid === currentUid
    if (isSelf && !isOwner) {
      setErrorMsg(t('NLSOpsChangesSelfApprovalBlocked'))
      return
    }

    if (isSelf && isOwner && decision === 'APPROVE') {
      setReasonTitle(t('NLSMemberOpsSelfApprovalReason'))
      setReasonValue('')
      setOnConfirmReason(() => async () => {
        await decideRosterChange({
          workspaceId,
          changeId: change.id,
          decision: 'APPROVE',
          selfApprovalReason: reasonValue.trim(),
        })
      })
      setReasonDialogOpen(true)
      return
    }

    setActionBusy(true)
    setErrorMsg(null)
    try {
      await decideRosterChange({
        workspaceId,
        changeId: change.id,
        decision,
      })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleDecideCorrection = async (
    correction: OpsAttendanceCorrection,
    decision: 'APPROVE' | 'REJECT',
  ) => {
    if (correction.requestedBy === currentUid) {
      setErrorMsg(t('NLSOpsCannotReviewOwnRecord'))
      return
    }

    setActionBusy(true)
    setErrorMsg(null)
    try {
      await decideAttendanceCorrection({
        workspaceId,
        attendanceId: correction.attendanceId,
        correctionId: correction.id,
        decision,
      })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  // ---------------------------------------------------------------------------
  // 2. ROSTER SECTION
  // ---------------------------------------------------------------------------
  const [rosters, setRosters] = useState<OpsRoster[]>([])
  const [latestVersion, setLatestVersion] = useState<OpsRosterVersion | null>(null)
  const [shifts, setShifts] = useState<OpsShift[]>([])

  useEffect(() => {
    if (!workspaceId || (!canRoster && !canReports) || !scope.ready) return
    const unsubRosters = subscribeRosters(
      workspaceId,
      undefined,
      (rList: OpsRoster[]) => setRosters(rList),
      (err) => console.error('[MemberOpsTab] subscribeRosters failed', err),
      { callerUid: currentUid, isRestricted: scope.restricted },
    )
    const unsubShifts = subscribeShifts(workspaceId, (sList) => setShifts(sList))
    return () => {
      unsubRosters()
      unsubShifts()
    }
  }, [workspaceId, canRoster, canReports, currentUid, scope.ready, scope.restricted])

  const activeRoster = rosters[0] || null

  useEffect(() => {
    if (!workspaceId || !activeRoster?.id || (!canRoster && !canReports) || !scope.ready) {
      return undefined
    }
    const unsub = subscribeRosterVersions(
      workspaceId,
      activeRoster.id,
      (vers) => setLatestVersion(vers[0] || null),
      (err) => setRosterError(getApiErrorMessage(err)),
      { callerUid: currentUid, isRestricted: scope.restricted },
    )
    return () => {
      unsub()
      setLatestVersion(null)
    }
  }, [workspaceId, activeRoster?.id, canRoster, canReports, currentUid, scope.ready, scope.restricted])

  const effectiveLatestVersion =
    !workspaceId || !activeRoster?.id || (!canRoster && !canReports)
      ? null
      : latestVersion

  const { startDayKey, endDayKey } = useMemo(() => getThisWeekAndNextRange(), [])

  const myShifts = useMemo(() => {
    if (!effectiveLatestVersion || !Array.isArray(effectiveLatestVersion.assignments)) return []
    return effectiveLatestVersion.assignments
      .filter(
        (a) =>
          a.personRef.kind === 'MEMBER' &&
          a.personRef.uid === currentUid &&
          a.dayKey >= startDayKey &&
          a.dayKey <= endDayKey,
      )
      .sort((a, b) => a.dayKey.localeCompare(b.dayKey))
  }, [effectiveLatestVersion, currentUid, startDayKey, endDayKey])

  const needsAck = useMemo(() => {
    return needsCallerAcknowledgement(effectiveLatestVersion ?? undefined, currentUid)
  }, [effectiveLatestVersion, currentUid])

  const handleAcknowledgeRosterVersion = async () => {
    if (!activeRoster || !effectiveLatestVersion) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await acknowledgeRoster({
        workspaceId,
        rosterId: activeRoster.id,
        version: effectiveLatestVersion.version,
      })
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  // ---------------------------------------------------------------------------
  // 3. REGISTERS SECTION
  // ---------------------------------------------------------------------------
  const [readableRecords, setReadableRecords] = useState<OpsRecord[]>([])
  const [approvedTemplates, setApprovedTemplates] = useState<OpsTemplate[]>([])
  const [selectedRecordToView, setSelectedRecordToView] = useState<OpsRecord | null>(null)
  const [newRecordModalOpen, setNewRecordModalOpen] = useState(false)

  useEffect(() => {
    if (!workspaceId || !canRegisters || !scope.ready) return
    const unsub = subscribeToRecords(
      workspaceId,
      {
        callerUid: currentUid,
        isReviewer: canReviewRecord,
        isEditor: isManager,
        isRestricted: scope.restricted,
      },
      (recs) => setReadableRecords(recs),
      (err) => setRegistersError(getApiErrorMessage(err)),
    )
    const unsubTpl = subscribeToTemplates(
      workspaceId,
      'construction',
      (tpls: OpsTemplate[]) => {
        setApprovedTemplates(
          tpls.filter((t) => !t.archived && (t.currentVersion ?? 0) > 0),
        )
      },
      (err) => setRegistersError(getApiErrorMessage(err)),
    )
    return () => {
      unsub()
      unsubTpl()
    }
  }, [workspaceId, canRegisters, currentUid, canReviewRecord, isManager, scope.ready, scope.restricted])

  // ---------------------------------------------------------------------------
  // 4. REQUESTS SECTION
  // ---------------------------------------------------------------------------
  const [opsRequests, setOpsRequests] = useState<OpsRequest[]>([])

  useEffect(() => {
    if (!workspaceId || (!canRequests && !canReports && !canExceptions && !canEquipment) || !scope.ready) return
    const unsub = subscribeOpsRequests(
      workspaceId,
      (reqs: OpsRequest[]) => setOpsRequests(reqs),
      (err: Error) => setRequestsError(getApiErrorMessage(err)),
      {
        callerUid: currentUid,
        isElevated: isAdmin,
        isRestricted: scope.restricted,
        viewerRoleKeys: mySectorRoles,
      },
    )
    return () => unsub()
  }, [workspaceId, canRequests, canReports, canExceptions, canEquipment, currentUid, isAdmin, scope.ready, scope.restricted, mySectorRoles])

  const viewerContext = useMemo(
    () => ({
      uid: currentUid,
      role: activeRole || 'VIEWER',
      isOwner,
      sectorRoles: mySectorRoles,
    }),
    [currentUid, activeRole, isOwner, mySectorRoles],
  )

  const handleRequestAction = async (
    req: OpsRequest,
    action: 'acknowledge' | 'start' | 'done' | 'verify' | 'reopen' | 'cancel',
  ) => {
    setActionBusy(true)
    setErrorMsg(null)
    const opId = crypto.randomUUID()
    try {
      if (action === 'acknowledge') {
        await acknowledgeRequest({ workspaceId, requestId: req.id, opId })
      } else if (action === 'start') {
        await startRequest({ workspaceId, requestId: req.id, opId })
      } else if (action === 'done') {
        await markRequestDone({ workspaceId, requestId: req.id, opId })
      } else if (action === 'verify') {
        await verifyRequest({ workspaceId, requestId: req.id, decision: 'APPROVE', opId })
      } else if (action === 'reopen') {
        setReasonTitle(t('NLSOpsRequestsActReopen'))
        setReasonValue('')
        setOnConfirmReason(() => async () => {
          await reopenRequest({
            workspaceId,
            requestId: req.id,
            reason: reasonValue.trim(),
            opId: crypto.randomUUID(),
          })
        })
        setReasonDialogOpen(true)
        return
      } else if (action === 'cancel') {
        await cancelRequest({
          workspaceId,
          requestId: req.id,
          reason: 'Cancelled by user',
          opId,
        })
      }
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  // ---------------------------------------------------------------------------
  // 5. REPORTS SECTION ("TODAY")
  // ---------------------------------------------------------------------------
  const [todayDues, setTodayDues] = useState<OpsDue[]>([])
  const [attendanceRecords, setAttendanceRecords] = useState<OpsAttendance[]>([])

  useEffect(() => {
    if (!workspaceId || (!canReports && !canApprovals && !canExceptions) || !scope.ready) return
    const { from, to } = getTodayRange()

    const unsubDues = subscribeDues(
      workspaceId,
      { workspaceId, from, to, callerUid: currentUid, isRestricted: scope.restricted },
      (dues: OpsDue[]) => setTodayDues(dues),
      (err) => setTodayError(getApiErrorMessage(err)),
    )

    const { startDayKey, endDayKey } = getPastSevenDaysRange()
    const unsubAtt = subscribeAttendance(
      workspaceId,
      {
        startDayKey,
        endDayKey,
        callerUid: currentUid,
        isManager,
        isMarker,
        isRestricted: scope.restricted,
      },
      (attList: OpsAttendance[]) => {
        setAttendanceRecords(attList)
      },
      (err) => setTodayError(getApiErrorMessage(err)),
    )

    return () => {
      unsubDues()
      unsubAtt()
    }
  }, [workspaceId, canReports, canApprovals, canExceptions, currentUid, isManager, isMarker, scope.ready, scope.restricted])

  // Key corrections by attendance IDs, unsubscribing on cleanup and updates (N3)
  const attendanceIdsKey = useMemo(
    () => attendanceRecords.map((a) => a.id).sort().join(','),
    [attendanceRecords],
  )

  useEffect(() => {
    if (!canApprovals) return
    const unsubs: Array<() => void> = []

    for (const att of attendanceRecords) {
      const u = subscribeAttendanceCorrections(
        att.id,
        (corrs) => {
          const pending = corrs
            .filter((c) => c.state === 'PENDING')
            .map((c) => ({ attendanceId: att.id, correction: c }))
          setPendingCorrections((prev) => {
            const others = prev.filter((p) => p.attendanceId !== att.id)
            return [...others, ...pending]
          })
        },
        (err) => setApprovalsError(getApiErrorMessage(err)),
      )
      unsubs.push(u)
    }

    return () => {
      unsubs.forEach((u) => u())
    }
  }, [canApprovals, attendanceIdsKey, attendanceRecords])

  const todayAttendance = useMemo(() => {
    const todayDayKey = getTodayDayKey()
    return attendanceRecords.filter((a) => a.dayKey === todayDayKey)
  }, [attendanceRecords])

  const reportCounts = useMemo(() => {
    const { from: todayStart, to: todayEnd } = getTodayRange()
    const todayDayKey = getTodayDayKey()
    const filteredTodayDues = todayDues.filter((d) => {
      if (d.periodKey) return d.periodKey === todayDayKey
      if (typeof d.windowStart === 'number') {
        return d.windowStart >= todayStart && d.windowStart <= todayEnd
      }
      return true
    })

    const duesDone = filteredTodayDues.filter((d) => d.status === 'DONE' || d.status === 'LATE_DONE').length
    const duesOpen = filteredTodayDues.filter((d) => d.status === 'OPEN').length
    const duesMissed = filteredTodayDues.filter((d) => d.status === 'MISSED').length

    const requestsOpen = opsRequests.filter(
      (r) => r.state === 'OPEN' || r.state === 'ACKNOWLEDGED',
    ).length
    const requestsInProgress = opsRequests.filter((r) => r.state === 'IN_PROGRESS').length

    const attendanceMarked = todayAttendance.filter((a) => isAttendanceEffective(a)).length
    const attendanceNotMarked = todayAttendance.filter((a) => !isAttendanceEffective(a)).length

    return {
      duesDone,
      duesOpen,
      duesMissed,
      requestsOpen,
      requestsInProgress,
      attendanceMarked,
      attendanceNotMarked,
    }
  }, [todayDues, opsRequests, todayAttendance])

  // ---------------------------------------------------------------------------
  // 6. EXCEPTIONS INBOX (m.exceptions)
  // ---------------------------------------------------------------------------
  const exceptions = useMemo(() => {
    if (!canExceptions || !activeUnitId) {
      return {
        missedEscalatedDues: [],
        skippedDues: [],
        openSpillRequests: [],
        openCriticalFaultRequests: [],
        blockedBeds: [],
      }
    }
    return unitExceptions({
      dues: todayDues,
      requests: opsRequests,
      assets: exceptionAssets,
      bedStates: exceptionBedStates,
      unitIds: activeUnitSubtreeIds,
      todayKey: getTodayDayKey(),
    })
  }, [
    canExceptions,
    activeUnitId,
    todayDues,
    opsRequests,
    exceptionAssets,
    exceptionBedStates,
    activeUnitSubtreeIds,
  ])

  const totalExceptions =
    exceptions.missedEscalatedDues.length +
    exceptions.skippedDues.length +
    exceptions.openSpillRequests.length +
    exceptions.openCriticalFaultRequests.length +
    exceptions.blockedBeds.length

  // ---------------------------------------------------------------------------
  // 7. EQUIPMENT DOWN NOW (m.equipment)
  // ---------------------------------------------------------------------------
  const downEquipment = useMemo(() => {
    return exceptionAssets
      .filter((a) => !a.retired && (a.status === 'ISOLATED' || a.status === 'MAINTENANCE'))
      .sort((a, b) => {
        const aTime = a.downSince ?? 0
        const bTime = b.downSince ?? 0
        return aTime - bTime
      })
  }, [exceptionAssets])

  const [releaseTarget, setReleaseTarget] = useState<OpsAsset | null>(null)
  const [releaseReason, setReleaseReason] = useState<string>('')
  const [isSelfRelease, setIsSelfRelease] = useState<boolean>(false)
  const [releaseBusy, setReleaseBusy] = useState<boolean>(false)

  const handleOpenRelease = (asset: OpsAsset, selfRelease: boolean) => {
    setReleaseTarget(asset)
    setReleaseReason('')
    setIsSelfRelease(selfRelease)
  }

  const handleConfirmRelease = async () => {
    if (!releaseTarget) return
    setReleaseBusy(true)
    setErrorMsg(null)
    try {
      const opId = createOpId()
      await releaseAsset({
        workspaceId,
        assetId: releaseTarget.id,
        toStatus: 'AVAILABLE',
        reason: releaseReason.trim() || undefined,
        selfRelease: isSelfRelease,
        expectedVersion: releaseTarget.version,
        opId,
      })
      setReleaseTarget(null)
      setReleaseReason('')
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setReleaseBusy(false)
    }
  }

  return (
    <div className={styles.container}>
      {errorMsg && (
        <div className={styles.errorBanner} role="alert">
          {errorMsg}
        </div>
      )}

      {/* 1. APPROVALS */}
      {canApprovals && (
        <section
          className={styles.section}
          data-testid="member-ops-section-approvals"
          aria-labelledby="member-approvals-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-approvals-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsApprovalsTitle')}
            </h2>
          </div>

          {approvalsError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-approvals-error">
              {approvalsError}
            </div>
          )}

          {submittedRecords.length === 0 &&
          cosignRecords.length === 0 &&
          pendingChanges.length === 0 &&
          pendingCorrections.length === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsEmptyApprovals')}</p>
          ) : (
            <ul className={styles.itemList}>
              {/* Record Review / Approvals */}
              {submittedRecords.map((rec) => {
                const isSelf = rec.authorUid === currentUid
                return (
                  <li key={`rec-${rec.id}`} className={styles.itemCard}>
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        {rec.templateId || t('NLSOpsTabRecords')}
                      </span>
                      <StatusPill tone="info">{rec.state}</StatusPill>
                    </div>
                    <div className={styles.itemMeta}>
                      <span>{rec.placeId}</span>
                    </div>
                    <div className={styles.itemActions}>
                      {canReviewRecord && (
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`approval-review-${rec.id}`}
                          disabled={actionBusy || (isSelf && !isOwner)}
                          onClick={() => void handleReviewRecord(rec.id || '')}
                        >
                          {t('NLSOpsActionReview')}
                        </Button>
                      )}
                      {canApproveRecord && (
                        <Button
                          variant="primary"
                          size="sm"
                          data-testid={`approval-approve-${rec.id}`}
                          disabled={actionBusy || (isSelf && !isOwner)}
                          onClick={() => void handleApproveRecord(rec)}
                        >
                          {t('NLSOpsActionApprove')}
                        </Button>
                      )}
                      {canReviewRecord && (
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`approval-return-${rec.id}`}
                          disabled={actionBusy || (isSelf && !isOwner)}
                          onClick={() => handleReturnRecordPrompt(rec.id || '')}
                        >
                          {t('NLSOpsActionReturn')}
                        </Button>
                      )}
                    </div>
                    {isSelf && !isOwner && (
                      <span className={styles.selfApprovalNotice}>
                        {t('NLSOpsCannotReviewOwnRecord')}
                      </span>
                    )}
                  </li>
                )
              })}

              {/* Co-Sign Approvals */}
              {cosignRecords.map((rec) => (
                <li key={`cosign-${rec.id}`} className={styles.itemCard}>
                  <div className={styles.itemTop}>
                    <span className={styles.itemTitle}>{rec.templateId || 'Co-Sign Record'}</span>
                    <StatusPill tone="warning">AWAITING CO-SIGN</StatusPill>
                  </div>
                  <div className={styles.itemActions}>
                    <Button
                      variant="primary"
                      size="sm"
                      data-testid={`approval-cosign-${rec.id}`}
                      disabled={actionBusy}
                      onClick={() => void handleCoSign(rec.id || '', true)}
                    >
                      {t('NLSMemberOpsCoSignAgree')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid={`approval-refuse-${rec.id}`}
                      disabled={actionBusy}
                      onClick={() => void handleCoSign(rec.id || '', false)}
                    >
                      {t('NLSMemberOpsCoSignRefuse')}
                    </Button>
                  </div>
                </li>
              ))}

              {/* Roster Changes */}
              {pendingChanges.map((change) => {
                const isSelf = change.requesterUid === currentUid
                return (
                  <li key={`change-${change.id}`} className={styles.itemCard}>
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        {change.type} — {change.dayKey}
                      </span>
                      <StatusPill tone="warning">{change.state}</StatusPill>
                    </div>
                    {change.reason && <div className={styles.itemMeta}>{change.reason}</div>}
                    <div className={styles.itemActions}>
                      <Button
                        variant="primary"
                        size="sm"
                        data-testid={`approval-change-approve-${change.id}`}
                        disabled={actionBusy || (isSelf && !isOwner)}
                        onClick={() => void handleDecideChange(change, 'APPROVE')}
                      >
                        {t('NLSOpsChangesApprove')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`approval-change-reject-${change.id}`}
                        disabled={actionBusy || (isSelf && !isOwner)}
                        onClick={() => void handleDecideChange(change, 'REJECT')}
                      >
                        {t('NLSOpsChangesReject')}
                      </Button>
                    </div>
                    {isSelf && !isOwner && (
                      <span className={styles.selfApprovalNotice}>
                        {t('NLSOpsChangesSelfApprovalBlocked')}
                      </span>
                    )}
                  </li>
                )
              })}

              {/* Attendance Corrections */}
              {pendingCorrections.map(({ correction }) => {
                const isSelf = correction.requestedBy === currentUid
                return (
                  <li key={`corr-${correction.id}`} className={styles.itemCard}>
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        Correction: {correction.requestedStatus}
                      </span>
                      <StatusPill tone="warning">{correction.state}</StatusPill>
                    </div>
                    {correction.reason && (
                      <div className={styles.itemMeta}>{correction.reason}</div>
                    )}
                    <div className={styles.itemActions}>
                      <Button
                        variant="primary"
                        size="sm"
                        data-testid={`approval-correction-approve-${correction.id}`}
                        disabled={actionBusy || isSelf}
                        onClick={() => void handleDecideCorrection(correction, 'APPROVE')}
                      >
                        {t('NLSOpsChangesApprove')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`approval-correction-reject-${correction.id}`}
                        disabled={actionBusy || isSelf}
                        onClick={() => void handleDecideCorrection(correction, 'REJECT')}
                      >
                        {t('NLSOpsChangesReject')}
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {isConstruction && scope.ready && (
            <MemberConApprovalsGroup
              workspaceId={workspaceId}
              callerUid={currentUid}
              scope={effectiveScope}
            />
          )}
        </section>
      )}

      {/* 2. ROSTER */}
      {canRoster && (
        <section
          className={styles.section}
          data-testid="member-ops-section-roster"
          aria-labelledby="member-roster-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-roster-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsRosterTitle')}
            </h2>
            {needsAck && (
              <Button
                variant="primary"
                size="sm"
                data-testid="member-roster-ack-btn"
                disabled={actionBusy}
                onClick={() => void handleAcknowledgeRosterVersion()}
              >
                {t('NLSMemberOpsAcknowledgeShift')}
              </Button>
            )}
          </div>

          {rosterError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-roster-error">
              {rosterError}
            </div>
          )}

          {myShifts.length === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsEmptyRoster')}</p>
          ) : (
            <ul className={styles.itemList}>
              {myShifts.map((assignment, idx) => {
                const shiftDef = shifts.find((s) => s.id === assignment.shiftId)
                return (
                  <li key={`shift-${assignment.dayKey}-${idx}`} className={styles.itemCard}>
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        {shiftDef?.name || assignment.shiftId}
                      </span>
                      <StatusPill tone="info">{assignment.dayKey}</StatusPill>
                    </div>
                    <div className={styles.itemMeta}>
                      {shiftDef?.startMinute !== undefined &&
                        shiftDef?.endMinute !== undefined && (
                          <span>
                            {formatShiftMinutes(shiftDef.startMinute)} –{' '}
                            {formatShiftMinutes(shiftDef.endMinute)}
                          </span>
                        )}
                      {assignment.dutyRoleKey && <span>Role: {assignment.dutyRoleKey}</span>}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* 3. REGISTERS */}
      {canRegisters && (
        <section
          className={styles.section}
          data-testid="member-ops-section-registers"
          aria-labelledby="member-registers-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-registers-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsRegistersTitle')}
            </h2>
            <Button
              variant="secondary"
              size="sm"
              data-testid="member-new-record-btn"
              onClick={() => setNewRecordModalOpen(true)}
            >
              {t('NLSMemberOpsNewRecord')}
            </Button>
          </div>

          {registersError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-registers-error">
              {registersError}
            </div>
          )}

          {selectedRecordToView ? (
            <RecordView
              record={selectedRecordToView}
              templateFields={[]}
              corrections={[]}
              photoUrlsByPath={{}}
              canAddCorrection={false}
              getTemplateName={(id) => id}
              getPlaceName={(id) => id}
              translateRecordState={(s) => s}
              onOpenCorrectionDialog={() => {}}
              onBackToList={() => setSelectedRecordToView(null)}
              currentUid={currentUid}
            />
          ) : readableRecords.length === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsEmptyRegisters')}</p>
          ) : (
            <ul className={styles.itemList}>
              {readableRecords.map((rec) => (
                <li
                  key={`record-${rec.id}`}
                  className={styles.itemCard}
                  onClick={() => setSelectedRecordToView(rec)}
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles.itemTop}>
                    <span className={styles.itemTitle}>
                      {rec.templateId || t('NLSOpsTabRecords')}
                    </span>
                    <StatusPill tone={rec.state === 'APPROVED' ? 'success' : 'neutral'}>
                      {rec.state}
                    </StatusPill>
                  </div>
                  <div className={styles.itemMeta}>
                    <span>{rec.placeId}</span>
                    <span>{new Date(Number(rec.occurredAt)).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <OpsRecordModalForm
            open={newRecordModalOpen}
            workspaceId={workspaceId}
            templates={approvedTemplates}
            places={places}
            formTestId="member-record-form"
            submitTestId="member-record-submit"
            submitLabel={t('NLSMemberOpsNewRecord')}
            onDismiss={() => setNewRecordModalOpen(false)}
            onSuccess={() => setNewRecordModalOpen(false)}
          />
        </section>
      )}

      {/* 4. REQUESTS */}
      {canRequests && (
        <section
          className={styles.section}
          data-testid="member-ops-section-requests"
          aria-labelledby="member-requests-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-requests-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsRequestsTitle')}
            </h2>
          </div>

          {requestsError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-requests-error">
              {requestsError}
            </div>
          )}

          {opsRequests.length === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsEmptyRequests')}</p>
          ) : (
            <ul className={styles.itemList}>
              {opsRequests.map((req) => {
                const actions = requestActionsFor(req, viewerContext)
                const isSelected = effectiveRequestId === req.id
                return (
                  <li
                    key={`req-${req.id}`}
                    className={styles.itemCard}
                    data-testid={`member-request-item-${req.id}`}
                    data-selected={isSelected ? 'true' : undefined}
                    aria-current={isSelected ? 'true' : undefined}
                    style={
                      isSelected
                        ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 2px var(--primary)' }
                        : undefined
                    }
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>{req.title}</span>
                      <StatusPill tone={req.state === 'DONE' ? 'success' : 'info'}>
                        {req.state}
                      </StatusPill>
                    </div>
                    <div className={styles.itemMeta}>
                      <span>{req.kindSnapshot?.label || req.kind}</span>
                      <span>{req.placeId}</span>
                    </div>
                    <div className={styles.itemActions}>
                      {actions.canAcknowledge && (
                        <Button
                          variant="primary"
                          size="sm"
                          data-testid={`request-ack-${req.id}`}
                          disabled={actionBusy}
                          onClick={() => void handleRequestAction(req, 'acknowledge')}
                        >
                          {t('NLSOpsRequestsActAcknowledge')}
                        </Button>
                      )}
                      {actions.canStart && (
                        <Button
                          variant="primary"
                          size="sm"
                          data-testid={`request-start-${req.id}`}
                          disabled={actionBusy}
                          onClick={() => void handleRequestAction(req, 'start')}
                        >
                          {t('NLSOpsRequestsActStart')}
                        </Button>
                      )}
                      {actions.canMarkDone && (
                        <Button
                          variant="primary"
                          size="sm"
                          data-testid={`request-done-${req.id}`}
                          disabled={actionBusy}
                          onClick={() => void handleRequestAction(req, 'done')}
                        >
                          {t('NLSOpsRequestsActMarkDone')}
                        </Button>
                      )}
                      {actions.canVerify && (
                        <Button
                          variant="primary"
                          size="sm"
                          data-testid={`request-verify-${req.id}`}
                          disabled={actionBusy}
                          onClick={() => void handleRequestAction(req, 'verify')}
                        >
                          {t('NLSOpsRequestsActVerify')}
                        </Button>
                      )}
                      {actions.canReopen && (
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`request-reopen-${req.id}`}
                          disabled={actionBusy}
                          onClick={() => void handleRequestAction(req, 'reopen')}
                        >
                          {t('NLSOpsRequestsActReopen')}
                        </Button>
                      )}
                      {actions.canCancel && (
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`request-cancel-${req.id}`}
                          disabled={actionBusy}
                          onClick={() => void handleRequestAction(req, 'cancel')}
                        >
                          {t('NLSOpsRequestsActCancel')}
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* 5. REPORTS ("TODAY") */}
      {canReports && (
        <section
          className={styles.section}
          data-testid="member-ops-section-reports"
          aria-labelledby="member-reports-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-reports-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsTodayTitle')}
            </h2>
          </div>

          {todayError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-today-error">
              {todayError}
            </div>
          )}

          <div className={styles.reportGrid}>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-dues-done">
                {reportCounts.duesDone}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsDuesDone')}</span>
            </div>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-dues-open">
                {reportCounts.duesOpen}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsDuesOpen')}</span>
            </div>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-dues-missed">
                {reportCounts.duesMissed}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsDuesMissed')}</span>
            </div>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-requests-open">
                {reportCounts.requestsOpen}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsRequestsOpen')}</span>
            </div>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-requests-in-progress">
                {reportCounts.requestsInProgress}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsRequestsInProgress')}</span>
            </div>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-attendance-marked">
                {reportCounts.attendanceMarked}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsAttendanceMarked')}</span>
            </div>
            <div className={styles.reportCard}>
              <span className={styles.reportCount} data-testid="report-count-attendance-unmarked">
                {reportCounts.attendanceNotMarked}
              </span>
              <span className={styles.reportLabel}>{t('NLSMemberOpsAttendanceNotMarked')}</span>
            </div>
          </div>

          {isConstruction && scope.ready && (
            <MemberConReportsGroup
              workspaceId={workspaceId}
              scope={effectiveScope}
            />
          )}
        </section>
      )}

      {/* 6. EXCEPTIONS INBOX (m.exceptions) */}
      {canExceptions && scope.ready && (
        <section
          className={styles.section}
          data-testid="member-ops-section-exceptions"
          aria-labelledby="member-exceptions-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-exceptions-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsExceptionsTitle')}
              {totalExceptions > 0 ? ` (${totalExceptions})` : ''}
            </h2>
            {unitPlaces.length > 0 && (
              <Select
                ariaLabel={t('NLSMemberOpsSelectUnit')}
                value={activeUnitId}
                onChange={(val) => setSelectedUnitId(String(val))}
                options={unitPlaces.map((u) => ({ value: u.id || '', label: u.name }))}
                data-testid="member-exceptions-unit-picker"
              />
            )}
          </div>

          {exceptionsError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-exceptions-error">
              {exceptionsError}
            </div>
          )}

          {unitPlaces.length === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsNoUnitsFound')}</p>
          ) : totalExceptions === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsExceptionsEmpty')}</p>
          ) : (
            <ul className={styles.itemList}>
              {exceptions.openSpillRequests.map((r) => (
                <li key={`exc-spill-${r.id}`} className={styles.itemCard}>
                  <a
                    href="?tab=hos-today"
                    className={styles.exceptionLink}
                    data-testid={`exception-row-${r.id}`}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>{r.title}</span>
                      <StatusPill tone="danger">SPILL</StatusPill>
                    </div>
                    <div className={styles.itemMeta}>
                      <span>{r.placeId}</span>
                    </div>
                  </a>
                </li>
              ))}

              {exceptions.openCriticalFaultRequests.map((r) => (
                <li key={`exc-fault-${r.id}`} className={styles.itemCard}>
                  <a
                    href="?tab=hos-today"
                    className={styles.exceptionLink}
                    data-testid={`exception-row-${r.id}`}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>{r.title}</span>
                      <StatusPill tone="danger">FAULT</StatusPill>
                    </div>
                    <div className={styles.itemMeta}>
                      <span>{r.placeId}</span>
                      {r.assetId && <span>{r.assetId}</span>}
                    </div>
                  </a>
                </li>
              ))}

              {exceptions.blockedBeds.map((b) => {
                const bedPlace = places.find((p) => p.id === b.placeId)
                return (
                  <li key={`exc-bed-${b.placeId}`} className={styles.itemCard}>
                    <a
                      href="?tab=hos-today"
                      className={styles.exceptionLink}
                      data-testid={`exception-row-bed-${b.placeId}`}
                    >
                      <div className={styles.itemTop}>
                        <span className={styles.itemTitle}>
                          {bedPlace?.name || b.placeId}
                        </span>
                        <StatusPill tone="warning">BLOCKED</StatusPill>
                      </div>
                      {b.blockReason && (
                        <div className={styles.itemMeta}>
                          <span>{b.blockReason}</span>
                        </div>
                      )}
                    </a>
                  </li>
                )
              })}

              {exceptions.missedEscalatedDues.map((d) => (
                <li key={`exc-missed-${d.id}`} className={styles.itemCard}>
                  <a
                    href="?tab=hos-today"
                    className={styles.exceptionLink}
                    data-testid={`exception-row-${d.id}`}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        {d.scheduleId || d.id}
                      </span>
                      <StatusPill tone="danger">{d.status}</StatusPill>
                    </div>
                    <div className={styles.itemMeta}>
                      <span>{d.placeId}</span>
                    </div>
                  </a>
                </li>
              ))}

              {exceptions.skippedDues.map((d) => (
                <li key={`exc-skipped-${d.id}`} className={styles.itemCard}>
                  <a
                    href="?tab=hos-today"
                    className={styles.exceptionLink}
                    data-testid={`exception-row-${d.id}`}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        {d.scheduleId || d.id}
                      </span>
                      <StatusPill tone="neutral">SKIPPED</StatusPill>
                    </div>
                    {d.skipReason && (
                      <div className={styles.itemMeta}>
                        <span>{d.skipReason}</span>
                      </div>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* 7. EQUIPMENT DOWN NOW (m.equipment) */}
      {canEquipment && scope.ready && (
        <section
          className={styles.section}
          data-testid="member-ops-section-equipment"
          aria-labelledby="member-equipment-title"
        >
          <div className={styles.sectionHeader}>
            <h2 id="member-equipment-title" className={styles.sectionTitle}>
              {t('NLSMemberOpsEquipmentTitle')}
              {downEquipment.length > 0 ? ` (${downEquipment.length})` : ''}
            </h2>
          </div>

          {equipmentError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-equipment-error">
              {equipmentError}
            </div>
          )}

          {downEquipment.length === 0 ? (
            <p className={styles.emptyState}>{t('NLSMemberOpsEquipmentEmpty')}</p>
          ) : (
            <ul className={styles.itemList}>
              {downEquipment.map((asset) => {
                const linkedFaultId = asset.downRequestId || asset.isolation?.requestId
                const linkedRequest = linkedFaultId
                  ? opsRequests.find((r) => r.id === linkedFaultId)
                  : undefined

                const hasOpenRequest = opsRequests.some(
                  (r) => r.assetId === asset.id && !OPS_REQUEST_CLOSED_STATES.has(r.state),
                )
                const lastDoneByUid =
                  linkedRequest?.doneByUid ||
                  (asset.isolation?.requestId
                    ? opsRequests.find((r) => r.id === asset.isolation?.requestId)?.doneByUid
                    : undefined)

                const actions = assetActionsFor(asset, viewerContext, {
                  hasOpenRequest,
                  lastDoneByUid,
                })

                const canRelease = actions.canRelease || actions.canSelfRelease
                const placeName = places.find((p) => p.id === asset.placeId)?.name || asset.placeId

                return (
                  <li
                    key={`equip-${asset.id}`}
                    className={styles.itemCard}
                    data-testid={`equipment-card-${asset.id}`}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle}>
                        {asset.assetCode} — {asset.name}
                      </span>
                      <StatusPill tone="danger">{asset.status}</StatusPill>
                    </div>

                    <div className={styles.itemMeta}>
                      <span>{asset.category || '—'}</span>
                      <span>{placeName}</span>
                      <span>
                        {asset.downSince
                          ? t('NLSMemberOpsDownSince', {
                              time: new Date(asset.downSince).toLocaleString(),
                            })
                          : t('NLSMemberOpsDownSinceUnknown')}
                      </span>
                      {linkedRequest && (
                        <span>{linkedRequest.title}</span>
                      )}
                    </div>

                    {canRelease && (
                      <div className={styles.itemActions}>
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`release-asset-${asset.id}`}
                          disabled={actionBusy || releaseBusy}
                          onClick={() => handleOpenRelease(asset, actions.canSelfRelease)}
                        >
                          {t('NLSMemberOpsReleaseAction')}
                        </Button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* 8. BED BOARD SECTION */}
      {canBeds && (
        <section className={styles.section} data-testid="member-ops-section-beds">
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              {t('NLSMemberOpsBedsTitle')}
            </h3>
          </div>

          {bedsError && (
            <div className={styles.errorBanner} role="alert" data-testid="member-beds-error">
              {bedsError}
            </div>
          )}

          {memberWards.length === 0 ? (
            <div className={styles.emptyNotice}>
              {t('NLSMemberOpsNoWardsFound')}
            </div>
          ) : (
            <>
              {memberWards.length > 0 && (
                <div className={styles.pickerRow}>
                  <Select
                    value={activeWardId}
                    onChange={(val) => setSelectedWardId(String(val))}
                    options={memberWards.map((w) => ({ value: w.id || '', label: w.name }))}
                    ariaLabel={t('NLSMemberOpsSelectWard')}
                  />
                </div>
              )}

              <div className={styles.stateCountsBar}>
                <div className={styles.countBadge}>
                  <span>{t('NLSMemberOpsCountAll')}: </span>
                  <strong data-testid="bed-count-all">{bedCounts.ALL}</strong>
                </div>
                <div className={styles.countBadge}>
                  <span>{t('NLSMemberOpsCountReady')}: </span>
                  <strong data-testid="bed-count-ready">{bedCounts.READY}</strong>
                </div>
                <div className={styles.countBadge}>
                  <span>{t('NLSMemberOpsCountOccupied')}: </span>
                  <strong data-testid="bed-count-occupied">{bedCounts.OCCUPIED}</strong>
                </div>
                <div className={styles.countBadge}>
                  <span>{t('NLSMemberOpsCountAwaitingClean')}: </span>
                  <strong data-testid="bed-count-awaiting-clean">{bedCounts.AWAITING_CLEAN}</strong>
                </div>
                <div className={styles.countBadge}>
                  <span>{t('NLSMemberOpsCountCleaning')}: </span>
                  <strong data-testid="bed-count-cleaning">{bedCounts.CLEANING}</strong>
                </div>
                <div className={styles.countBadge}>
                  <span>{t('NLSMemberOpsCountBlocked')}: </span>
                  <strong data-testid="bed-count-blocked">{bedCounts.BLOCKED}</strong>
                </div>
              </div>

              {roomsInWard.length === 0 && bedsInWard.length === 0 ? (
                <div className={styles.emptyNotice}>
                  {t('NLSMemberOpsBedsEmpty')}
                </div>
              ) : (
                <div className={styles.bedListContainer}>
                  {roomsInWard.map((room) => {
                    const beds = bedsInWard.filter(
                      (b) =>
                        b.parentId === room.id ||
                        (b.path && b.path[b.path.length - 2] === room.id),
                    )
                    if (beds.length === 0) return null
                    return (
                      <div key={room.id} className={styles.roomGroup}>
                        <h4 className={styles.roomTitle}>{room.name}</h4>
                        <div className={styles.bedList}>
                          {beds.map((bed) => {
                            const bedId = bed.id || ''
                            const state = (bedId ? bedStateMap.get(bedId)?.state : undefined) || 'READY'
                            return (
                              <div key={bedId} className={styles.bedCard}>
                                <div className={styles.bedHeader}>
                                  <span className={styles.bedName}>{bed.name}</span>
                                  <span
                                    className={styles.bedStateChip}
                                    data-testid={`bed-chip-${bedId}`}
                                  >
                                    {state}
                                  </span>
                                </div>
                                {canManageBeds && (
                                  <div className={styles.bedActions}>
                                    {state === 'READY' && (
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        data-testid={`bed-action-mark-occupied-${bedId}`}
                                        onClick={() => handleMarkOccupied(bedId)}
                                        disabled={bedActionBusy}
                                      >
                                        {t('NLSMemberOpsActionMarkOccupied')}
                                      </Button>
                                    )}
                                    {state === 'OCCUPIED' && (
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        data-testid={`bed-action-discharge-${bedId}`}
                                        onClick={() => setBedDischargeTarget(bed)}
                                        disabled={bedActionBusy}
                                      >
                                        {t('NLSMemberOpsActionDischarge')}
                                      </Button>
                                    )}
                                    {state !== 'BLOCKED' && (
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        data-testid={`bed-action-block-${bedId}`}
                                        onClick={() => {
                                          setBedBlockTarget(bed)
                                          setBedBlockReason('')
                                        }}
                                        disabled={bedActionBusy}
                                      >
                                        {t('NLSMemberOpsActionBlock')}
                                      </Button>
                                    )}
                                    {state === 'BLOCKED' && (
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        data-testid={`bed-action-unblock-${bedId}`}
                                        onClick={() => handleUnblock(bedId)}
                                        disabled={bedActionBusy}
                                      >
                                        {t('NLSMemberOpsActionUnblock')}
                                      </Button>
                                    )}
                                    {(state === 'AWAITING_CLEAN' || state === 'CLEANING') &&
                                      canOverrideReady && (
                                        <Button
                                          size="sm"
                                          variant="secondary"
                                          data-testid={`bed-action-override-${bedId}`}
                                          onClick={() => {
                                            setBedOverrideTarget(bed)
                                            setBedOverrideReason('')
                                          }}
                                          disabled={bedActionBusy}
                                        >
                                          {t('NLSMemberOpsActionOverride')}
                                        </Button>
                                      )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* m.bmw: Biomedical Waste Register */}
      {canBmw && (
        <BmwPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          callerRole={activeRole}
          isOwner={isOwner}
          isManager={canCorrectBmw}
          sectorRoles={mySectorRoles}
          allowCorrection={true}
          places={places}
        />
      )}

      {/* m.oncall: Hospital On-Call Duty */}
      {canOnCall && (
        <OnCallPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          callerRole={activeRole}
          surface="member"
        />
      )}

      {/* m.dpr: Construction Member DPR Review */}
      {canDpr && scope.ready && (
        <MemberDprPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          scope={effectiveScope}
        />
      )}

      {/* m.inspections: Construction Member Inspections */}
      {canInspections && scope.ready && (
        <MemberInspectionsPhoneSection
          workspaceId={workspaceId}
          callerUid={currentUid}
          scope={effectiveScope}
        />
      )}

      {/* m.plant: Plant Operations */}
      {canPlant && scope.ready && (
        <PlantMemberPhoneSection
          workspaceId={workspaceId}
          assets={exceptionAssets}
          requests={opsRequests}
          currentUid={currentUid}
          isReleaser={isAdmin || mySectorRoles.includes('ASSET_RELEASER')}
        />
      )}

      {/* Bed Discharge Dialog */}
      <AppDialog
        open={Boolean(bedDischargeTarget)}
        title={t('NLSMemberOpsActionDischarge')}
        onDismiss={() => setBedDischargeTarget(null)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setBedDischargeTarget(null),
            disabled: bedActionBusy,
          },
          {
            label: t('NLSCommonActionConfirm'),
            variant: 'primary',
            onClick: handleConfirmDischarge,
            disabled: bedActionBusy,
          },
        ]}
      >
        <div className={styles.dialogBody}>
          <p>
            {t('NLSMemberOpsDischargeConfirm')}
          </p>
        </div>
      </AppDialog>

      {/* Bed Block Dialog */}
      <AppDialog
        open={Boolean(bedBlockTarget)}
        title={t('NLSMemberOpsBlockConfirmTitle')}
        onDismiss={() => {
          setBedBlockTarget(null)
          setBedBlockReason('')
        }}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => {
              setBedBlockTarget(null)
              setBedBlockReason('')
            },
            disabled: bedActionBusy,
          },
          {
            label: t('NLSCommonActionConfirm'),
            variant: 'primary',
            onClick: handleConfirmBlock,
            disabled: bedActionBusy || bedBlockReason.trim().length < 3,
          },
        ]}
      >
        <div className={styles.dialogBody}>
          <TextInput
            value={bedBlockReason}
            onChange={(val) => setBedBlockReason(val)}
            placeholder={t('NLSMemberOpsBlockReasonPlaceholder')}
          />
        </div>
      </AppDialog>

      {/* Bed Override Dialog */}
      <AppDialog
        open={Boolean(bedOverrideTarget)}
        title={t('NLSMemberOpsOverrideConfirmTitle')}
        onDismiss={() => {
          setBedOverrideTarget(null)
          setBedOverrideReason('')
        }}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => {
              setBedOverrideTarget(null)
              setBedOverrideReason('')
            },
            disabled: bedActionBusy,
          },
          {
            label: t('NLSCommonActionConfirm'),
            variant: 'primary',
            onClick: handleConfirmOverride,
            disabled: bedActionBusy || bedOverrideReason.trim().length < 3,
          },
        ]}
      >
        <div className={styles.dialogBody}>
          <TextInput
            value={bedOverrideReason}
            onChange={(val) => setBedOverrideReason(val)}
            placeholder={t('NLSMemberOpsOverrideReasonPlaceholder')}
          />
        </div>
      </AppDialog>

      {/* Release Confirmation Dialog */}
      <AppDialog
        open={Boolean(releaseTarget)}
        title={t('NLSMemberOpsReleaseConfirmTitle')}
        onDismiss={() => setReleaseTarget(null)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setReleaseTarget(null),
            disabled: releaseBusy,
          },
          {
            label: t('NLSCommonActionConfirm'),
            variant: 'primary',
            onClick: handleConfirmRelease,
            disabled: releaseBusy,
          },
        ]}
      >
        <div className={styles.dialogBody}>
          <TextInput
            value={releaseReason}
            onChange={(val) => setReleaseReason(val)}
            placeholder={t('NLSMemberOpsReleaseReasonPlaceholder')}
          />
        </div>
      </AppDialog>

      {/* Confirmation / Reason Modal Dialog */}
      <AppDialog
        open={reasonDialogOpen}
        title={reasonTitle}
        onDismiss={() => setReasonDialogOpen(false)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setReasonDialogOpen(false),
            disabled: actionBusy,
          },
          {
            label: t('NLSCommonActionConfirm'),
            variant: 'primary',
            onClick: async () => {
              if (onConfirmReason) {
                setActionBusy(true)
                setErrorMsg(null)
                try {
                  await onConfirmReason()
                  setReasonDialogOpen(false)
                } catch (err) {
                  setErrorMsg(getApiErrorMessage(err))
                } finally {
                  setActionBusy(false)
                }
              }
            },
            disabled: actionBusy || !reasonValue.trim(),
          },
        ]}
      >
        <div className={styles.dialogBody}>
          <TextInput
            value={reasonValue}
            onChange={(val) => setReasonValue(val)}
            placeholder={reasonTitle}
          />
        </div>
      </AppDialog>
    </div>
  )
}
